import React, { useState } from 'react';
import { Table, Button, Row, Col, Form, Pagination } from 'react-bootstrap';
import { FaEdit, FaPlus, FaTrash } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import Loader from '../components/Loader';
import Message from '../components/Message';

import {
  useGetStoreIncomesQuery,
  useDeleteStoreIncomeMutation,
  useCreateStoreIncomeMutation,
} from '../slices/storeIncomesApiSlice';
import { toast } from 'react-toastify';

const CashUserList = () => {
  
  const [pageNumber, setPageNumber] = useState(1);
  const { data, isLoading, error, refetch } = useGetStoreIncomesQuery({ pageNumber });
  const [deleteStoreIncome, { isLoading: loadingDelete }] = useDeleteStoreIncomeMutation();
  const [createStoreIncome, { isLoading: loadingCreate }] = useCreateStoreIncomeMutation();

  const [filter, setFilter] = useState('');
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Create a new storeIncome
  const createStoreIncomeHandler = async () => {
    if (window.confirm('Are you sure you want to create a new storeIncome?')) {
      try {
        const newStoreIncome = {
          date: new Date().toISOString().split('T')[0],
          account: 'Debit',
          accountName: 'Default Account',
          subAccount: 'Default Sub Account',
          details: 'New StoreIncome',
          amount: 0,
          status: 'Pending',
          category: 'Other',
          notes: 'Default note',
        
        };
        await createStoreIncome(newStoreIncome).unwrap();
        toast.success('New storeIncome created successfully');
        refetch();
      } catch (err) {
        toast.error(err?.data?.message || err.error);
      }
    }
  };

  // Delete an storeIncome
  const deleteHandler = async (id) => {
    if (window.confirm('Are you sure you want to delete this storeIncome?')) {
      try {
        await deleteStoreIncome(id).unwrap();
        toast.success('StoreIncome deleted successfully');
        refetch();
      } catch (err) {
        toast.error(err?.data?.message || err.error);
      }
    }
  };

  // Handle sorting
  const handleSort = (field) => {
    const order = sortField === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortOrder(order);
  };

  // Filter and sort storeIncomes
  const filterAndSortStoreIncomes = () => {
    let filteredStoreIncomes = [...(data?.storeIncomes || [])];

    // Filter by text input
    if (filter) {
      filteredStoreIncomes = filteredStoreIncomes.filter(
        (storeIncome) =>
          storeIncome.account?.toLowerCase().includes(filter.toLowerCase()) ||
          storeIncome.accountName?.toLowerCase().includes(filter.toLowerCase()) ||
          storeIncome.details?.toLowerCase().includes(filter.toLowerCase()) ||
          storeIncome.category?.toLowerCase().includes(filter.toLowerCase()) ||
          storeIncome.subAccount?.toLowerCase().includes(filter.toLowerCase()) ||
          storeIncome.notes?.toLowerCase().includes(filter.toLowerCase())
      );
    }

    // Filter by date range
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      filteredStoreIncomes = filteredStoreIncomes.filter((storeIncome) => {
        const storeIncomeDate = new Date(storeIncome.date);
        return storeIncomeDate >= start && storeIncomeDate <= end;
      });
    }

    // Sort storeIncomes
    if (sortField) {
      filteredStoreIncomes.sort((a, b) => {
        if (sortField === 'amount') {
          return sortOrder === 'asc' ? a.amount - b.amount : b.amount - a.amount;
        } else if (sortField === 'date') {
          return sortOrder === 'asc'
            ? new Date(a.date) - new Date(b.date)
            : new Date(b.date) - new Date(a.date);
        } else {
          const aValue = a[sortField]?.toLowerCase() || '';
          const bValue = b[sortField]?.toLowerCase() || '';
          return sortOrder === 'asc'
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }
      });
    }

    return filteredStoreIncomes;
  };

  // Calculate total for date range
  const calculateTotalForDateRange = () => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);

    const filteredStoreIncomes = data?.storeIncomes?.filter((storeIncome) => {
      const storeIncomeDate = new Date(storeIncome.date);
      return storeIncomeDate >= start && storeIncomeDate <= end;
    }) || [];

    return filteredStoreIncomes.reduce((acc, storeIncome) => acc + storeIncome.amount, 0);
  };

  // Calculate total for all storeIncomes
  const totalAmount = data?.storeIncomes?.reduce((acc, storeIncome) => acc + storeIncome.amount, 0) || 0;

   // Handle pagination changes
   const paginationHandler = (page) => {
    setPageNumber(page);
  };

  
  return (
    <>
      <Row className="align-items-center">
        <Col>
          <h1>Mini Cash Collected</h1>
        </Col>
        <Col className="text-end">
          <Button onClick={createStoreIncomeHandler} className="my-3" style={{display:"none"}}>
            <FaPlus /> Add New StoreIncome
          </Button>
          <Link to='/create/staff/storeIncome' className='btn btn-light my-3'>
           Add Data
          </Link>
          <Link to='/create/income/excelform' className='btn btn-light my-3'>
           AddFromExcel
          </Link>
        </Col>
      </Row>
  
      <Row className="mb-3">
        <Col>
          <h5>Total Amount: D{totalAmount.toFixed(2)}</h5>
        </Col>
        <Col md={4}>
          <Form.Control
            type="text"
            placeholder="Search by account, name, details, category, subAccount, or notes"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </Col>
      </Row>
  
      <Row className="mb-3">
        <Col md={4}>
          <Form.Control
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            placeholder="Start Date"
          />
        </Col>
        <Col md={4}>
          <Form.Control
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            placeholder="End Date"
          />
        </Col>
        <Col md={4}>
          <h5>
            Total (Filtered Range): D{calculateTotalForDateRange().toFixed(2)}
          </h5>
        </Col>
      </Row>
  
      {loadingCreate && <Loader />}
      {loadingDelete && <Loader />}
      {isLoading ? (
        <Loader />
      ) : error ? (
        <Message variant="danger">{error?.data?.message || 'An error occurred'}</Message>
      ) : data?.storeIncomes?.length > 0 ? (
        <>
          <Table striped bordered hover responsive className="table-sm">
            <thead>
              <tr>
                <th onClick={() => handleSort('date')} style={{ cursor: 'pointer' }}>
                  Date {sortField === 'date' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                
                <th onClick={() => handleSort('accountName')} style={{ cursor: 'pointer' }}>
                COLLECTOR {sortField === 'accountName' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                
                <th onClick={() => handleSort('amount')} style={{ cursor: 'pointer' }}>
                  Amount {sortField === 'amount' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th onClick={() => handleSort('category')} style={{ cursor: 'pointer' }}>
                  Category {sortField === 'category' ? (sortOrder === 'asc' ? '↑' : '↓') : ''}
                </th>
                <th>Images</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filterAndSortStoreIncomes().map((storeIncome) => (
                <tr key={storeIncome._id}>
                  <td>{new Date(storeIncome.date).toLocaleDateString()}</td>
                
                  <td>{storeIncome.accountName}</td>
                
                  <td>
                    {storeIncome.amount >= 0
                      ? `${storeIncome.amount.toFixed(2)}`
                      : `-${Math.abs(storeIncome.amount).toFixed(2)}`}
                  </td>
                  <td>{storeIncome.category}</td>
                  
                  <td className="thumbnails">
                    {storeIncome.images?.length > 0 && (
                      <img
                        src={storeIncome.images[0]}
                        alt="Thumbnail"
                        className="thumbnail-img me-2"
                        style={{
                          width: '15px',
                          height: '15px',
                          objectFit: 'cover',
                        }}
                      />
                    )}
                  </td>

                  <td>
                    <Link
                     className="btn btn-primary btn-sm m-2"
                    to={`/staffstoreIncome/${storeIncome._id}`}
                    role="button"
                    >
                       Details
                    </Link>
                    <Button
                      style={{display:"none"}}
                      as={Link}
                      to={`/storeIncome/${storeIncome._id}/edit`}
                      variant="light"
                      className="btn-sm mx-2"
                    >
                      <FaEdit />
                    </Button>
                    <Button
                     style={{display: "none"}}
                      variant="danger"
                      className="btn-sm"
                      onClick={() => deleteHandler(storeIncome._id)}
                    >
                      <FaTrash />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
           <Pagination>
                                  {[...Array(data.pages).keys()].map((x) => (
                                    <Pagination.Item
                                      key={x + 1}
                                      active={x + 1 === pageNumber}
                                      onClick={() => paginationHandler(x + 1)}
                                    >
                                      {x + 1}
                                    </Pagination.Item>
                                  ))}
             </Pagination>
           
          
        </>
      ) : (
        <Message variant="info">No storeIncomes found</Message>
      )}
    </>
  );
};

export default CashUserList;